import React, { useMemo, useCallback, useRef } from 'react';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Manager, Reference, Popper } from '@atlaskit/popper';
import { Box } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ColumnPickerContainer,
	useColumnPickerOpenState,
} from '@atlassian/jira-issue-table-column-picker/src/controllers/column-picker-open-state/index.tsx';
import { useVisibleDepth } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import AsyncColumnPicker from '@atlassian/jira-issue-table-column-picker/src/ui/async.tsx';
import type { Props as AsyncColumnPickerProps } from '@atlassian/jira-issue-table-column-picker/src/ui/main.tsx';
import { useInlineFieldCreateOnboardingContext } from '@atlassian/jira-native-issue-table/src/ui/inline-field-create-onboarding/index.tsx';
import { getExpandColumnWidth } from '../utils.tsx';
import {
	useInlineCreateActiveIndexActions,
	useInlineFieldCreateActiveIndex,
} from '../../../controllers/inline-create-active-index/index.tsx';
import { FIELD } from '../../../controllers/inline-create-active-index/types.tsx';
import {
	COLUMN_ID_EXPAND_BUTTON,
	COLUMN_ID_CHECKBOX,
	INSERT_COLUMN_BUTTON_Y_OFFSET,
	COLUMN_ID_INTERNAL_COLUMN,
	COLUMN_PICKER_ADD_COLUMN_CONTAINER,
} from '../../../common/constants.tsx';
import type { Column } from '../../../common/types.tsx';
import { useIsInlineFieldConfigEnabled } from '../../../controllers/features/selectors.tsx';
import { IFCFooter } from '../../../common/ui/inline-field-config/column-picker-footer/index.tsx';

export type InsertColumnButtonProps = {
	columns: Column[];
	columnPickerProps?: AsyncColumnPickerProps;
};

// Height/width of the popup button
const DEFAULT_BUTTON_SIZE = 24;
// Height/width of the reference element the popup button is anchored to
const REFERENCE_SIZE = 1;

const constantModifiers = [
	{
		name: 'flip',
		enabled: false,
	},
];

export const InsertColumnButtonInner = ({
	columns,
	columnPickerProps,
}: InsertColumnButtonProps) => {
	const [{ index: columnIndex }] = useInlineFieldCreateActiveIndex();
	const [{ isOpen }, { close }] = useColumnPickerOpenState();
	const { lockIndex, clearIndex, clearIndexAfterDelay, setIndex } =
		useInlineCreateActiveIndexActions();
	const { showInlineFieldCreateOnboarding } = useInlineFieldCreateOnboardingContext();
	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
	let visibleDepth = 0;
	if (fg('jsc_m2_hierarchy_fe_changes')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		visibleDepth = useVisibleDepth();
	}

	const firstFieldColumnIndex = useMemo(() => {
		return columns.findIndex((column) => !column.id.startsWith(COLUMN_ID_INTERNAL_COLUMN)) - 1;
	}, [columns]);

	const hasShownOnboarding = useRef(false);

	const shouldShowOnboarding = showInlineFieldCreateOnboarding && !hasShownOnboarding.current;

	if (shouldShowOnboarding) {
		setIndex(FIELD, firstFieldColumnIndex);
		lockIndex(FIELD);
		hasShownOnboarding.current = true;
	}

	const columnsWidth = useMemo(
		() =>
			columns.map((column) => {
				if (column.id === COLUMN_ID_EXPAND_BUTTON) {
					return getExpandColumnWidth(visibleDepth);
				}
				return column.customWidth ?? column.defaultWidth;
			}),
		[columns, visibleDepth],
	);

	const fixedColumnCount = useMemo(
		() =>
			columns.filter(
				(column) => column.id === COLUMN_ID_EXPAND_BUTTON || column.id === COLUMN_ID_CHECKBOX,
			).length,
		[columns],
	);

	const isEmptyColumnExist = columnsWidth.length - columns.length === 1;

	const insertButtonXOffset = useMemo(() => {
		if (columnIndex != null && columnsWidth.length > 0) {
			return columnsWidth
				.slice(0, columnIndex + 1)
				.reduce((acc, width) => acc + width, -REFERENCE_SIZE);
		}
		return 0;
	}, [columnIndex, columnsWidth]);

	const finalOffset: [number, number] = useMemo(
		() =>
			// Negatively offset the popup by the button + reference size to center the button on the secondary axis
			[
				insertButtonXOffset,
				INSERT_COLUMN_BUTTON_Y_OFFSET - (DEFAULT_BUTTON_SIZE + REFERENCE_SIZE) / 2,
			],
		[insertButtonXOffset],
	);

	const modifiers = useMemo(() => {
		// Adjust the overflow calculation for the primary axis so it is relative to the center of the button.
		const alongCenterOffset = -(DEFAULT_BUTTON_SIZE / 2);
		// Retain the provided offset for the secondary axis
		const padding = { top: finalOffset[1], left: alongCenterOffset, right: alongCenterOffset };
		return [
			...constantModifiers,
			{
				name: 'preventOverflow',
				options: {
					mainAxis: true,
					altAxis: true,
					padding,
					altBoundary: true,
					rootBoundary: 'viewport',
					tether: false,
				},
			},
			{
				name: 'eventListeners',
				options: {
					// Only recompute position on scroll/resize events while the button is focused to reduce CPU load
					scroll: isOpen,
					resize: isOpen,
				},
			},
		];
	}, [finalOffset, isOpen]);

	const handleMouseEnter = useCallback(() => {
		lockIndex(FIELD);
	}, [lockIndex]);

	const handleMouseLeave = useCallback(() => {
		!isOpen && clearIndexAfterDelay(FIELD);
	}, [clearIndexAfterDelay, isOpen]);

	const shouldNotShowInsertButton = useMemo(
		() =>
			columnIndex == null ||
			(isEmptyColumnExist && columnIndex >= columnsWidth.length - 3) || // If an empty column exists, prevent inserting in the last two columns.
			columnIndex >= columnsWidth.length - 2 || // Prevent inserting on the left or right of the last column.
			columnIndex < fixedColumnCount - 1,
		[columnIndex, columnsWidth.length, fixedColumnCount, isEmptyColumnExist],
	);

	if (shouldNotShowInsertButton) {
		if (!shouldShowOnboarding) {
			clearIndex(FIELD);
			close();
		}
	}

	if (columnPickerProps) {
		const { components, ...props } = columnPickerProps;
		return (
			<Manager>
				<Reference>
					{({ ref }) => <Box xcss={cx(styles.reference, styles.visuallyHidden)} ref={ref} />}
				</Reference>
				<Popper placement="bottom" offset={finalOffset} modifiers={modifiers}>
					{({ ref, style }) => {
						const styleProps = { style };
						return (
							<Box
								ref={ref}
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...styleProps}
								onMouseEnter={handleMouseEnter}
								onMouseLeave={handleMouseLeave}
								xcss={cx(styles.button, shouldNotShowInsertButton && styles.hide)}
								testId={`native-issue-table.ui.issue-table.insert-column-button.popper${shouldNotShowInsertButton ? '-hidden' : ''}`}
							>
								<AsyncColumnPicker
									{...props}
									components={{
										...components,
										...(isInlineFieldConfigEnabled ? { Footer: IFCFooter } : {}),
									}}
									columnInsertIndex={columnIndex ? columnIndex - fixedColumnCount + 1 : undefined}
									isInBetweenColumnPickerEnabled
									showInlineFieldCreateOnboarding={
										columnIndex === firstFieldColumnIndex && showInlineFieldCreateOnboarding
									}
									isInfiniteScrollEnabled
								/>
							</Box>
						);
					}}
				</Popper>
			</Manager>
		);
	}
	return null;
};

export const InsertColumnButton = (props: InsertColumnButtonProps) => {
	return (
		<ColumnPickerContainer scope={COLUMN_PICKER_ADD_COLUMN_CONTAINER}>
			<InsertColumnButtonInner {...props} />
		</ColumnPickerContainer>
	);
};

const styles = cssMap({
	button: {
		zIndex: 300,
	},
	reference: {
		left: token('space.0'),
		top: token('space.0'),
	},
	hide: {
		display: 'none',
	},
	visuallyHidden: {
		width: '1px',
		height: '1px',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		position: 'absolute',
		border: 'none',
		clipPath: 'rect(1px 1px 1px 1px)',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
});
